import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Search as SearchIcon } from 'lucide-react';
import api from '@/lib/api';
import { SearchInput, Card, Loader } from '@/components/ui';

interface SearchResult {
  id: number;
  name: string;
  name_latin: string;
  slug: string;
  image_url: string;
  short_description: string;
}

export function Search() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Include lang in queryKey so cache is invalidated on language switch
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ru';

  const { data: results, isLoading } = useQuery({
    queryKey: ['search', searchTerm, lang],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];
      const { data } = await api.get<SearchResult[]>('/api/animals/search', {
        params: { q: searchTerm },
      });
      return data;
    },
    enabled: searchTerm.length >= 2,
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <div className="relative z-10 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <SearchIcon className="w-12 h-12 mx-auto mb-4 text-cosmic-purple" />
        <h1 className="text-3xl font-bold mb-2">{t('search.title')}</h1>
        <p className="text-gray-400">
          {t('search.subtitle')}
        </p>
      </motion.div>

      {/* Search input */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="max-w-xl mx-auto"
      >
        <SearchInput
          value={query}
          onChange={setQuery}
          onSearch={handleSearch}
          placeholder={t('search.placeholder')}
          loading={isLoading}
          className="text-lg"
        />
      </motion.div>

      {/* Results */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center py-12"
          >
            <Loader text={t('search.searching')} />
          </motion.div>
        ) : searchTerm.length >= 2 && results ? (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {results.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl mb-4 block">🔍</span>
                <h2 className="text-xl font-bold mb-2">{t('search.notFound')}</h2>
                <p className="text-gray-400">
                  {t('search.notFoundHint')}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  {t('search.found')}: {results.length}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {results.map((animal, index) => (
                    <motion.div
                      key={animal.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link to={`/animal/${animal.slug}`}>
                        <Card
                          image={animal.image_url}
                          imageAlt={animal.name}
                          hover
                        >
                          <h3 className="font-semibold text-white">
                            {animal.name}
                          </h3>
                          <p className="text-sm text-gray-500 italic">
                            {animal.name_latin}
                          </p>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-12 text-gray-500"
          >
            <p>{t('search.startTyping')}</p>
            <p className="text-sm mt-2">{t('search.minChars')}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popular searches */}
      <section className="max-w-2xl mx-auto">
        <h2 className="text-lg font-semibold mb-4 text-center">
          {t('search.popularQueries')}
        </h2>
        <div className="flex flex-wrap justify-center gap-2">
          {(lang === 'en'
          ? ['Lion', 'Elephant', 'Dolphin', 'Eagle', 'Tiger', 'Penguin']
          : ['Лев', 'Слон', 'Дельфин', 'Орёл', 'Тигр', 'Пингвин']
        ).map((term) => (
            <button
              key={term}
              onClick={() => {
                setQuery(term);
                setSearchTerm(term);
              }}
              className="px-4 py-2 bg-space-700 hover:bg-space-600 rounded-full text-sm transition-colors"
            >
              {term}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
